import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CardSystemEditor from "../elementEditors/CardSystemEditor";
import { getSegmentDefinition, SEGMENT_TYPES } from "../segmentRegistry";
import {
  ColorSetting,
  EditorGroup,
  NumberSetting,
  PageEditorShell,
  PagePreviewTarget,
  PageHiddenTarget,
  PageEditorContextIntro,
  PageEditorBackButton,
  SelectSetting,
  TextSetting,
  ToggleSetting,
} from "./PageEditorControls";

function pageValue(form) {
  return form?.catalog?.productsPage || {};
}

function cardValue(form) {
  return form?.catalog?.cards || {};
}

function headerBackground(page, theme) {
  if (page.headerBackgroundColor) return page.headerBackgroundColor;
  return `radial-gradient(circle at 15% 20%, ${theme?.primaryColor || "#E44E31"} 0%, transparent 34%), linear-gradient(125deg, ${theme?.secondaryColor || "#494A98"}, #17182d)`;
}

const TARGETS = Object.freeze({
  page: { label: "Página", hint: "Fondo, superficies y estados" },
  eyebrow: { label: "Texto superior", hint: "Copy del encabezado" },
  title: { label: "Título", hint: "Título general y variantes" },
  subtitle: { label: "Subtítulo", hint: "Descripción del catálogo" },
  results: { label: "Resultados", hint: "Singular y plural" },
  search: { label: "Buscador", hint: "Visibilidad y placeholder" },
  filters: { label: "Filtros", hint: "Visibilidad y etiquetas" },
  sort: { label: "Orden", hint: "Orden predeterminado" },
  pagination: { label: "Paginación", hint: "Modo y cantidad por página" },
  cards: { label: "Cards del catálogo", hint: "Sistema compartido" },
});

function targetMeta(targetId) {
  return TARGETS[targetId] || TARGETS.page;
}


function ProductsPreviewTarget({ targetId, activeTarget, onSelect, label, children, sx = {} }) {
  return (
    <PagePreviewTarget
      targetId={targetId}
      activeTarget={activeTarget}
      onSelect={onSelect}
      label={label || targetMeta(targetId).label}
      sx={sx}
    >
      {children}
    </PagePreviewTarget>
  );
}

function sortPreviewLabel(page) {
  const by = page.sortBy || "createdAt";
  const dir = page.sortDir || "desc";
  const byLabel = by === "price" ? "Precio" : by === "name" ? "Nombre" : "Más recientes";
  return `${byLabel} · ${dir === "asc" ? "Ascendente" : "Descendente"}`;
}

function ProductsPagePreview({ form, activeTarget, onSelectTarget }) {
  const page = pageValue(form);
  const cards = cardValue(form);
  const theme = form?.theme || {};
  const textColor = page.textColor || theme.textColor || "#1A1A2E";
  const mutedText = page.mutedTextColor || theme.mutedTextColor || "#5B5D73";
  const surface = page.surfaceColor || theme.surfaceColor || "#FFFFFF";
  const background = page.backgroundColor || theme.backgroundColor || "#EEF0F5";
  const headerText = page.headerTextColor || "#FFFFFF";
  const cardBackground = cards.backgroundColor || surface;

  return (
    <Box
      sx={{ minHeight: 680, bgcolor: background, color: textColor, position: "relative" }}
      onClick={() => onSelectTarget("page")}
    >
      <Button
        size="small"
        variant={activeTarget === "page" ? "contained" : "outlined"}
        startIcon={<TuneRoundedIcon />}
        onClick={(event) => {
          event.stopPropagation();
          onSelectTarget("page");
        }}
        sx={{ position: "absolute", zIndex: 8, top: 10, right: 10, bgcolor: activeTarget === "page" ? undefined : "rgba(255,255,255,.94)" }}
      >
        Página
      </Button>

      <Box sx={{ px: { xs: 2, md: 5 }, pt: { xs: 7, md: 9 }, pb: 5, color: headerText, background: headerBackground(page, theme) }}>
        <Stack spacing={1.1}>
          <ProductsPreviewTarget targetId="eyebrow" activeTarget={activeTarget} onSelect={onSelectTarget}>
            <Typography data-preview-passive="true" variant="overline" sx={{ color: "inherit", opacity: page.eyebrow ? 0.72 : 0.48, fontStyle: page.eyebrow ? "normal" : "italic" }}>
              {page.eyebrow || "Texto superior"}
            </Typography>
          </ProductsPreviewTarget>

          <ProductsPreviewTarget targetId="title" activeTarget={activeTarget} onSelect={onSelectTarget}>
            <Typography data-preview-passive="true" variant="h3" sx={{ color: "inherit", fontWeight: 900 }}>
              {page.title || form?.navigation?.productsLabel || "Productos y servicios"}
            </Typography>
          </ProductsPreviewTarget>

          <ProductsPreviewTarget targetId="subtitle" activeTarget={activeTarget} onSelect={onSelectTarget}>
            <Typography data-preview-passive="true" sx={{ color: "inherit", opacity: page.subtitle ? 0.82 : 0.48, fontStyle: page.subtitle ? "normal" : "italic" }}>
              {page.subtitle || "Subtítulo opcional"}
            </Typography>
          </ProductsPreviewTarget>

          <ProductsPreviewTarget targetId="results" activeTarget={activeTarget} onSelect={onSelectTarget}>
            <Typography data-preview-passive="true" sx={{ color: "inherit", opacity: 0.72 }}>
              12 {page.resultPlural || "resultados"}
            </Typography>
          </ProductsPreviewTarget>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 1.5, md: 4 }, pb: 5 }}>
        {page.showSearch !== false ? (
          <ProductsPreviewTarget targetId="search" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ mt: -2.5 }}>
            <Box data-preview-passive="true" sx={{ p: 1.4, bgcolor: surface, borderRadius: 2.5, boxShadow: "0 14px 36px rgba(25,36,47,.14)" }}>
              <TextField
                fullWidth
                size="small"
                disabled
                placeholder={page.searchPlaceholder || "Buscar por nombre, servicio, código, categoría o subcategoría"}
                InputProps={{ startAdornment: <SearchRoundedIcon sx={{ mr: 1, color: mutedText }} /> }}
              />
            </Box>
          </ProductsPreviewTarget>
        ) : (
          <Box sx={{ mt: -2.5 }}><PageHiddenTarget targetId="search" activeTarget={activeTarget} onSelect={onSelectTarget} label="Buscador" /></Box>
        )}

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end" sx={{ mt: 2.4, mb: -1 }}>
          <ProductsPreviewTarget targetId="sort" activeTarget={activeTarget} onSelect={onSelectTarget}>
            <Chip data-preview-passive="true" size="small" icon={<TuneRoundedIcon />} label={`Orden · ${sortPreviewLabel(page)}`} />
          </ProductsPreviewTarget>
          <ProductsPreviewTarget targetId="pagination" activeTarget={activeTarget} onSelect={onSelectTarget}>
            <Chip data-preview-passive="true" size="small" variant="outlined" label={`${Number(page.pageSize) || 24} por página`} />
          </ProductsPreviewTarget>
        </Stack>

        <Box sx={{ mt: 3, display: "grid", gridTemplateColumns: page.showFilters === false ? "1fr" : { xs: "1fr", md: "220px 1fr" }, gap: 2.5 }}>
          {page.showFilters !== false ? (
            <ProductsPreviewTarget targetId="filters" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ alignSelf: "start" }}>
              <Box data-preview-passive="true" sx={{ bgcolor: surface, borderRadius: 2.5, p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <FilterListRoundedIcon fontSize="small" />
                  <Typography fontWeight={900}>{page.filtersLabel || "Filtros"}</Typography>
                </Stack>
                <Stack spacing={1.2}>
                  {page.showTypeFilter !== false ? <Chip label={page.typeFilterLabel || "Tipo"} variant="outlined" /> : null}
                  {page.showCategoryFilter !== false ? <Chip label={page.categoryFilterLabel || "Categoría"} variant="outlined" /> : null}
                  {page.showSubcategoryFilter !== false ? <Chip label={page.subcategoryFilterLabel || "Subcategoría"} variant="outlined" /> : null}
                </Stack>
              </Box>
            </ProductsPreviewTarget>
          ) : (
            <PageHiddenTarget targetId="filters" activeTarget={activeTarget} onSelect={onSelectTarget} label="Filtros" />
          )}

          <Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0,1fr))", md: "repeat(3, minmax(0,1fr))" }, gap: `${Number(cards.gap) || 18}px` }}>
              {["Producto destacado", "Servicio principal", "Nueva colección", "Más vendido", "Producto especial", "Servicio premium"].map((name, index) => (
                <ProductsPreviewTarget key={name} targetId="cards" activeTarget={activeTarget} onSelect={onSelectTarget} label="Cards del catálogo">
                  <Box
                    data-preview-passive="true"
                    sx={{
                      overflow: "hidden",
                      borderRadius: `${Number(cards.radius) || 16}px`,
                      bgcolor: cardBackground,
                      border: cards.borderColor ? `1px solid ${cards.borderColor}` : "1px solid rgba(25,36,47,.08)",
                    }}
                  >
                    {cards.showImage !== false ? <Box sx={{ height: 112, bgcolor: "rgba(25,36,47,.10)" }} /> : null}
                    <Box sx={{ p: 1.5 }}>
                      {cards.showCategory !== false ? (
                        <Typography variant="caption" sx={{ color: cards.categoryColor || theme.primaryColor || "#F35A3A", fontWeight: 800 }}>
                          Categoría
                        </Typography>
                      ) : null}
                      {cards.showTitle !== false ? (
                        <Typography sx={{ mt: 0.3, fontWeight: 900, color: cards.titleColor || textColor }}>
                          {name}
                        </Typography>
                      ) : null}
                      {cards.showPrice !== false ? (
                        <Typography sx={{ mt: 1, fontWeight: 900, color: cards.priceColor || theme.secondaryColor || "#19242F" }}>
                          $ {index % 2 ? "25.000" : "18.500"}
                        </Typography>
                      ) : null}
                      {cards.showButton !== false ? (
                        <Button tabIndex={-1} size="small" variant="outlined" fullWidth sx={{ mt: 1.2, pointerEvents: "none" }}>
                          {cards.ctaLabel || "Ver detalle"}
                        </Button>
                      ) : null}
                    </Box>
                  </Box>
                </ProductsPreviewTarget>
              ))}
            </Box>

            <ProductsPreviewTarget targetId="pagination" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ mt: 3 }}>
              <Box data-preview-passive="true" sx={{ display: "flex", justifyContent: "center" }}>
                {page.paginationMode === "loadMore" ? (
                  <Button tabIndex={-1} variant="outlined" sx={{ pointerEvents: "none" }}>
                    {page.loadMoreLabel || "Cargar más"}
                  </Button>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button tabIndex={-1} size="small" variant="outlined" sx={{ pointerEvents: "none" }}>Anterior</Button>
                    <Typography variant="body2" sx={{ color: mutedText }}>Página 1 de 4</Typography>
                    <Button tabIndex={-1} size="small" variant="outlined" sx={{ pointerEvents: "none" }}>Siguiente</Button>
                  </Stack>
                )}
              </Box>
            </ProductsPreviewTarget>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function PageCopyEditor({ targetId, page, set }) {
  if (targetId === "eyebrow") {
    return <TextSetting label="Texto superior" value={page.eyebrow} onChange={(value) => set("eyebrow", value)} />;
  }
  if (targetId === "subtitle") {
    return <TextSetting label="Subtítulo" value={page.subtitle} onChange={(value) => set("subtitle", value)} multiline />;
  }
  if (targetId === "results") {
    return (
      <EditorGroup title="Cómo mostramos la cantidad" description="Preview: 1 resultado / 12 resultados">
        <TextSetting label="Singular" value={page.resultSingular} onChange={(value) => set("resultSingular", value)} />
        <TextSetting label="Plural" value={page.resultPlural} onChange={(value) => set("resultPlural", value)} />
      </EditorGroup>
    );
  }
  return (
    <Stack spacing={1.5}>
      <TextSetting label="Título general" value={page.title} onChange={(value) => set("title", value)} helperText="Vacío = usar el nombre del catálogo de la navegación." />
      <EditorGroup title="Título según el tipo de catálogo" description="Se usa cuando el título general está vacío y corresponde distinguir el catálogo.">
        <TextSetting label="Productos" value={page.productsTitle} onChange={(value) => set("productsTitle", value)} />
        <TextSetting label="Servicios" value={page.servicesTitle} onChange={(value) => set("servicesTitle", value)} />
        <TextSetting label="Productos + servicio" value={page.hybridTitle} onChange={(value) => set("hybridTitle", value)} />
      </EditorGroup>
    </Stack>
  );
}

function SearchEditor({ page, set }) {
  return (
    <Stack spacing={1.4}>
      <ToggleSetting label="Mostrar buscador" checked={page.showSearch} onChange={(value) => set("showSearch", value)} />
      <TextSetting label="Texto dentro del buscador" value={page.searchPlaceholder} onChange={(value) => set("searchPlaceholder", value)} />
    </Stack>
  );
}

function FiltersEditor({ page, set }) {
  return (
    <Stack spacing={1.4}>
      <ToggleSetting label="Mostrar filtros" checked={page.showFilters} onChange={(value) => set("showFilters", value)} />
      <EditorGroup title="Qué filtros mostrar">
        <ToggleSetting label="Tipo" checked={page.showTypeFilter} onChange={(value) => set("showTypeFilter", value)} />
        <ToggleSetting label="Categoría" checked={page.showCategoryFilter} onChange={(value) => set("showCategoryFilter", value)} />
        <ToggleSetting label="Subcategoría" checked={page.showSubcategoryFilter} onChange={(value) => set("showSubcategoryFilter", value)} />
      </EditorGroup>
      <EditorGroup title="Textos de filtros">
        <TextSetting label="Título" value={page.filtersLabel} onChange={(value) => set("filtersLabel", value)} />
        <TextSetting label="Tipo" value={page.typeFilterLabel} onChange={(value) => set("typeFilterLabel", value)} />
        <TextSetting label="Categoría" value={page.categoryFilterLabel} onChange={(value) => set("categoryFilterLabel", value)} />
        <TextSetting label="Subcategoría" value={page.subcategoryFilterLabel} onChange={(value) => set("subcategoryFilterLabel", value)} />
        <Divider />
        <TextSetting label="Todos los tipos" value={page.allTypesLabel} onChange={(value) => set("allTypesLabel", value)} />
        <TextSetting label="Todas las categorías" value={page.allCategoriesLabel} onChange={(value) => set("allCategoriesLabel", value)} />
        <TextSetting label="Todas las subcategorías" value={page.allSubcategoriesLabel} onChange={(value) => set("allSubcategoriesLabel", value)} />
        <TextSetting label="Limpiar filtros" value={page.resetLabel} onChange={(value) => set("resetLabel", value)} />
        <TextSetting label="Aplicar filtros" value={page.applyFiltersLabel} onChange={(value) => set("applyFiltersLabel", value)} />
      </EditorGroup>
    </Stack>
  );
}

function SortEditor({ page, set }) {
  return (
    <Stack spacing={1.4}>
      <Typography variant="body2" sx={{ color: "rgba(25,36,47,.64)" }}>
        Este es el orden inicial con el que se abre el catálogo.
      </Typography>
      <SelectSetting
        label="Ordenar por"
        value={page.sortBy || "createdAt"}
        onChange={(value) => set("sortBy", value)}
        options={[
          { value: "createdAt", label: "Más recientes" },
          { value: "price", label: "Precio" },
          { value: "name", label: "Nombre" },
        ]}
      />
      <SelectSetting
        label="Dirección"
        value={page.sortDir || "desc"}
        onChange={(value) => set("sortDir", value)}
        options={[
          { value: "desc", label: "Descendente" },
          { value: "asc", label: "Ascendente" },
        ]}
      />
    </Stack>
  );
}

function PaginationEditor({ page, set }) {
  return (
    <Stack spacing={1.4}>
      <SelectSetting
        label="Comportamiento"
        value={page.paginationMode || "all"}
        onChange={(value) => set("paginationMode", value)}
        options={[
          { value: "all", label: "Páginas" },
          { value: "loadMore", label: "Cargar más" },
        ]}
      />
      <NumberSetting
        label="Resultados por página"
        value={page.pageSize}
        min={12}
        max={60}
        onChange={(value) => set("pageSize", value)}
        helperText="Elegí entre 12 y 60 resultados por página."
      />
      <TextSetting label="Botón Cargar más" value={page.loadMoreLabel} onChange={(value) => set("loadMoreLabel", value)} />
    </Stack>
  );
}

function PageSurfaceEditor({ page, set }) {
  return (
    <Stack spacing={1.5}>
      <EditorGroup title="Apariencia de la página" description="Las cards se editan tocando una card de ejemplo.">
        <ColorSetting label="Fondo" value={page.backgroundColor} onChange={(value) => set("backgroundColor", value)} />
        <ColorSetting label="Superficies" value={page.surfaceColor} onChange={(value) => set("surfaceColor", value)} />
        <ColorSetting label="Texto" value={page.textColor} onChange={(value) => set("textColor", value)} />
        <ColorSetting label="Texto secundario" value={page.mutedTextColor} onChange={(value) => set("mutedTextColor", value)} />
        <ColorSetting label="Fondo del encabezado" value={page.headerBackgroundColor} onChange={(value) => set("headerBackgroundColor", value)} />
        <ColorSetting label="Texto del encabezado" value={page.headerTextColor} onChange={(value) => set("headerTextColor", value)} />
      </EditorGroup>
      <EditorGroup title="Mensajes y estados" description="Sólo aparecen cuando el catálogo carga o no encuentra resultados.">
        <TextSetting label="Cargando" value={page.loadingText} onChange={(value) => set("loadingText", value)} />
        <TextSetting label="Título sin resultados" value={page.emptyTitle} onChange={(value) => set("emptyTitle", value)} />
        <TextSetting label="Texto sin resultados" value={page.emptyText} onChange={(value) => set("emptyText", value)} multiline />
        <TextSetting label="Botón sin resultados" value={page.emptyResetLabel} onChange={(value) => set("emptyResetLabel", value)} />
      </EditorGroup>
    </Stack>
  );
}

function catalogCardsDescriptor() {
  const definition = getSegmentDefinition(SEGMENT_TYPES.FEATURED);
  const fields = (definition?.properties || []).filter((property) => String(property?.path || "").startsWith("catalog.cards."));
  return {
    kind: "card-system",
    label: "Cards del catálogo",
    fieldPrefix: "catalog.cards.",
    fields,
  };
}

export default function ProductsPageEditor({
  form,
  device,
  saving = false,
  onCommit,
  onUploadImage,
  onRemoveImage,
}) {
  const page = pageValue(form);
  const [selectedTarget, setSelectedTarget] = React.useState("page");
  const set = React.useCallback((key, value) => onCommit?.(`catalog.productsPage.${key}`, value), [onCommit]);
  const cardsDescriptor = React.useMemo(catalogCardsDescriptor, []);
  const selectedMeta = targetMeta(selectedTarget);

  let editor = null;
  if (["eyebrow", "title", "subtitle", "results"].includes(selectedTarget)) {
    editor = <PageCopyEditor targetId={selectedTarget} page={page} set={set} />;
  } else if (selectedTarget === "search") {
    editor = <SearchEditor page={page} set={set} />;
  } else if (selectedTarget === "filters") {
    editor = <FiltersEditor page={page} set={set} />;
  } else if (selectedTarget === "sort") {
    editor = <SortEditor page={page} set={set} />;
  } else if (selectedTarget === "pagination") {
    editor = <PaginationEditor page={page} set={set} />;
  } else if (selectedTarget === "cards") {
    editor = (
      <CardSystemEditor
        descriptor={cardsDescriptor}
        form={form}
        disabled={saving}
        onCommit={onCommit}
        onUploadImage={onUploadImage}
        onRemoveImage={onRemoveImage}
      />
    );
  } else {
    editor = <PageSurfaceEditor page={page} set={set} />;
  }

  return (
    <PageEditorShell
      title={`Catálogo › ${selectedMeta.label}`}
      description={selectedMeta.hint}
      device={device}
      saving={saving}
      preview={<ProductsPagePreview form={form} activeTarget={selectedTarget} onSelectTarget={setSelectedTarget} />}
    >
      {selectedTarget !== "page" ? (
        <PageEditorBackButton
          onClick={() => setSelectedTarget("page")}
          startIcon={<ArrowBackRoundedIcon />}
        />
      ) : (
        <PageEditorContextIntro>
          Tocá cualquier elemento del preview para editar sólo ese componente.
        </PageEditorContextIntro>
      )}
      {editor}
    </PageEditorShell>
  );
}
